import apiSlice from '../apiSlice';
import { GlobalFilterKeys } from '../types';
import { stripGlobalFilterKeys } from '../utils';

import type {
  AudienceLibrary,
  AudienceLibraryRequest,
  AudienceLibrarySearchParams,
} from '@/features/audienceLibrary';

import { PaginationResponse } from '@/shared/utils/types';

const AUDIENCE_LIBRARIES_API_URL = '/api/audience-libraries';

const audienceLibraryTags = apiSlice.enhanceEndpoints({
  addTagTypes: ['audienceLibrary'],
});

const audienceLibraryApiSlice = audienceLibraryTags.injectEndpoints({
  endpoints: (builder) => ({
    getAudienceLibraries: builder.query<
      PaginationResponse<AudienceLibrary>,
      GlobalFilterKeys & AudienceLibrarySearchParams
    >({
      query: (params) => ({
        url: `${AUDIENCE_LIBRARIES_API_URL}/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      providesTags: [{ type: 'audienceLibrary', id: 'LIST' }],
    }),

    getAllAudienceLibraries: builder.query<
      Pick<AudienceLibrary, 'id' | 'audienceId' | 'name'>[],
      GlobalFilterKeys
    >({
      query: () => ({
        url: `${AUDIENCE_LIBRARIES_API_URL}/name-list/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'audienceLibrary', id: 'LIST' }],
    }),

    createAudienceLibrary: builder.mutation<
      AudienceLibrary,
      AudienceLibraryRequest
    >({
      query: (data) => ({
        url: `${AUDIENCE_LIBRARIES_API_URL}/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'audienceLibrary', id: 'LIST' }],
    }),

    updateAudienceLibrary: builder.mutation<
      AudienceLibrary,
      { id: number } & AudienceLibraryRequest
    >({
      query: ({ id, ...data }) => ({
        url: `${AUDIENCE_LIBRARIES_API_URL}/${String(id)}/`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: [{ type: 'audienceLibrary', id: 'LIST' }],
    }),

    archiveAudienceLibrary: builder.mutation<AudienceLibrary, { id: number }>({
      query: ({ id }) => ({
        url: `${AUDIENCE_LIBRARIES_API_URL}/${String(id)}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'audienceLibrary', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAudienceLibrariesQuery,
  useGetAllAudienceLibrariesQuery,
  useCreateAudienceLibraryMutation,
  useUpdateAudienceLibraryMutation,
  useArchiveAudienceLibraryMutation,
} = audienceLibraryApiSlice;
